/* eslint-disable react/prop-types */
import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback
} from 'react';
import { MapContainer, TileLayer, Polygon, Marker, Popup } from 'react-leaflet';
import AppContext from 'context/Context';
import 'leaflet.tilelayer.colorfilter';
import 'leaflet/dist/leaflet.css';
import '@changey/react-leaflet-markercluster/dist/styles.min.css';
import L from 'leaflet';
import { isEmpty } from 'lodash';
import { getColorScale, legendTitle } from 'helpers/leaflet-utils';
import Legend from 'components/common/map/Legend';
import GeotiffLayer from 'components/common/map/GeoTiffLayer';
import markerIcon from './../../../assets/img/icons/location.png';
import { MarkerClusterGroup } from 'leaflet';

const LeafletMap = React.memo(({ data, index, ...rest }) => {
  const {
    config: { isRTL }
  } = useContext(AppContext);

  const { coordinates, mapData, legendData, type, label } = data;
  const [indexScale, setIndexScale] = useState([]);
  const [layerVisible, setLayerVisible] = useState(true);

  useEffect(() => {
    setIndexScale(getColorScale(label));
  }, [label]);

  const position = useMemo(() => coordinates[0]?.[0], [coordinates]);

  const fillColor = useMemo(
    () => ({
      color: 'white',
      fillColor: '#fff',
      opacity: 1,
      fillOpacity: 0
    }),
    []
  );

  const tiffUrl = mapData.url;

  const legendTitleMap = useMemo(
    () => (isEmpty(legendTitle[label]) ? label : legendTitle[label]),
    [mapData.label]
  );

  const customIcon = useMemo(
    () =>
      L.icon({
        iconUrl: markerIcon,
        iconSize: [30, 30],
        iconAnchor: [12, 41],
        popupAnchor: [1, -34]
      }),
    []
  );

  const setLayerVisibleCallback = useCallback(visible => {
    setLayerVisible(visible);
  }, []);

  if (isEmpty(tiffUrl)) {
    return null;
  }

  return (
    <MapContainer
      zoom={isRTL ? 1.8 : 1.5}
      minZoom={isRTL ? 1.8 : 1.3}
      zoomSnap={0.5}
      center={position || [0, 0]}
      {...rest}
      radius={200}
    >
      <TileLayer
        attribution='&copy; <a href="https://www.mapbox.com/about/maps/">Mapbox</a>'
        url={`https://api.mapbox.com/styles/v1/mapbox/satellite-streets-v12/tiles/{z}/{x}/{y}?access_token=${process.env.REACT_APP_MAPBOX_PUBLIC_KEY}`}
      />
      {!isEmpty(coordinates) && (
        <Polygon pathOptions={fillColor} positions={coordinates} />
      )}

      {mapData?.label === 'Soil Organic Carbon' && type === 'dummy' && (
        <MarkerClusterGroup>
          {mapData?.data?.map((pointer, idx) => (
            <Marker
              position={[pointer.lat, pointer.lon]}
              icon={customIcon}
              key={idx}
            >
              <Popup closeButton={false}>SOC: {pointer.soc}%</Popup>
            </Marker>
          ))}
        </MarkerClusterGroup>
      )}

      {!isEmpty(tiffUrl) && (
        <GeotiffLayer
          key={mapData.label}
          url={tiffUrl}
          title={mapData.label}
          coordinates={coordinates}
          layerVisible={layerVisible}
          legendData={legendData}
        />
      )}

      {!isEmpty(tiffUrl) && (indexScale || legendData) && type !== 'dummy' && (
        <Legend
          indexScale={legendData || indexScale}
          title={legendTitleMap}
          setLayerVisible={setLayerVisibleCallback}
          area={mapData?.area}
        />
      )}
    </MapContainer>
  );
});

export default LeafletMap;
